import { useState } from 'react';
import { HAIR_OPTIONS, GENDER_OPTIONS, AGE_OPTIONS } from '../constants';
import DetailMenuManagementComponent from '../../../../1D/DetailMenuManagementPage/DetailMenuManagementComponent';

export const NiitaHeader = () => (
  <div className="flex items-center gap-2 mb-2 px-1">
    <span className="material-symbols-outlined text-indigo-500 text-2xl">psychology</span>
    <h3 className="text-lg font-bold text-slate-800">Niitaによるアドバイス</h3>
    <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full ml-auto">
      Niita Analysis
    </span>
  </div>
);

export const NiitaInterpretationBlock = ({ advice, metricsColors = {} }) => {
  return (
    <div className="bg-white rounded-xl shadow-medical border border-slate-200 overflow-hidden flex flex-col">
      <div className="bg-indigo-50/50 px-4 py-3 border-b border-indigo-100">
        <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
          <span className="material-symbols-outlined !text-lg">analytics</span>
          INTERPRETATION <span className="text-xs font-normal text-indigo-600 ml-1">（分析結果の解釈）</span>
        </h4>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm leading-relaxed text-slate-700 mb-4 font-medium">{advice?.summary}</p>
        <div className="space-y-3">
          {advice?.items.map((item) => (
            <div
              key={item.code}
              className="flex items-start gap-3 text-xs border-l-2 pl-3"
              style={{ borderColor: metricsColors[item.code] ?? '#0EA5E9' }}
            >
              <span className="text-slate-500 font-mono whitespace-nowrap w-12">{item.code}</span>
              <span className="text-slate-700">{item.text}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 text-right">
          <p className="text-[10px] text-slate-400">※美容ケアの参考情報です（医療診断ではありません）</p>
        </div>
      </div>
    </div>
  );
};

export const NiitaRecommendationBlock = ({ recommendations }) => {
  const [selectedMenuId, setSelectedMenuId] = useState(null);

  if (!recommendations?.length) return null;
  return (
    <div>
      <div className="bg-white rounded-xl shadow-medical border border-slate-200 overflow-hidden flex flex-col">
        <div className="bg-amber-50/50 px-4 py-3 border-b border-amber-100">
          <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">spa</span>
            RECOMMENDATION <span className="text-xs font-normal text-amber-700 ml-1">（推奨メニュー）</span>
          </h4>
        </div>
        <div className="p-5">
          <ul className="space-y-4">
            {recommendations.map((rec, index) => (
              <li key={index} className="relative pl-4">
                <button type="button" onClick={() => setSelectedMenuId(rec.menuId)}>
                  <div className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-amber-400"></div>
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-bold text-slate-800">{rec.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">{rec.description}</p>
                  <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded inline-block">
                    理由: {rec.reason}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {selectedMenuId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setSelectedMenuId(null)}
          >
            <div
              className="relative w-[90vw] max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close modal"
                onClick={() => setSelectedMenuId(null)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>

              <DetailMenuManagementComponent
                menuId={selectedMenuId}
                onNotFound={() => setSelectedMenuId(null)}
                onError={() => setSelectedMenuId(null)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const NiitaPersonalizationBlock = ({ inHairSize, inGender, inAge, onUpdate }) => {
  function mapInHairSizeToValue(hairSizeString) {
    const foundItem = HAIR_OPTIONS.find((c) => c.label === hairSizeString);
    if (foundItem) {
      return foundItem.value;
    }

    return 'normal';
  }

  function mapInGenderToValue(genderString) {
    const foundItem = GENDER_OPTIONS.find((c) => c.label === genderString);
    if (foundItem) {
      return foundItem.value;
    }

    return 'other';
  }

  function mapInAgeToValue(ageNumber) {
    if (typeof ageNumber !== 'number') return 20;

    // Get decade (e.g. 34 -> 30)
    const decade = Math.floor(ageNumber / 10) * 10;

    // Check if it exists in options
    const match = AGE_OPTIONS.find((opt) => opt.value === decade);

    return match ? match.value : 20;
  }

  const [hairThickness, setHairThickness] = useState(mapInHairSizeToValue(inHairSize));
  const [gender, setGender] = useState(mapInGenderToValue(inGender));
  const [age, setAge] = useState(mapInAgeToValue(inAge));
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    if (isUpdating) {
      return false;
    }
    setIsUpdating(true);

    try {
      await onUpdate({ hairThickness, gender, age });
    } catch (error) {
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-medical border border-slate-200 overflow-hidden flex flex-col">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
          <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">tune</span>
            PERSONALIZATION <span className="text-xs font-normal text-slate-500 ml-1">（個別最適化・任意）</span>
          </h4>
        </div>
        <div className="p-5">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">髪の太さ</label>
              <div className="flex bg-slate-100 rounded-lg p-1">
                {HAIR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setHairThickness(option.value)}
                    className={`flex-1 text-xs py-1.5 rounded-md transition ${
                      hairThickness === option.value
                        ? 'shadow-sm bg-white font-bold text-sky-500 border border-slate-200'
                        : 'text-slate-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1.5 block">性別</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs rounded-lg py-2 pl-3 pr-8 focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1.5 block">年齢</label>
                <select
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs rounded-lg py-2 pl-3 pr-8 focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  {AGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleUpdate}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold py-2.5 rounded-lg shadow-sm transition flex items-center justify-center gap-2 mt-2"
              disabled={isUpdating}
            >
              {isUpdating ? (
                '更新中…'
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">refresh</span>
                  Niitaのコメントを更新
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
